import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import VehicleBasicInfo from './components/VehicleBasicInfo';
import VehicleConditionAssessment from './components/VehicleConditionAssessment';
import VehiclePhotoUpload from './components/VehiclePhotoUpload';
import PreliminaryValuation from './components/PreliminaryValuation';
import FormProgressIndicator from './components/FormProgressIndicator';
import PersonalInformation from './components/PersonalInformation';
import.meta.env.VITE_API_URL;

const VehicleInformationUpload = () => {
  const apiUrl = import.meta.env.VITE_API_URL;

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValuationCollapsed, setIsValuationCollapsed] = useState(false);
  const [displayValuation, setDisplayValuation] = useState(false);

  const steps = [
    {
      id: 'basic-info',
      title: 'Basic Information',
      description: 'Vehicle details and specifications',
      component: VehicleBasicInfo,
      requiredFields: ['make', 'model', 'year', 'mileage', 'transmission', 'fuelType', 'color', 'cubic_capacity']
    },
    {
      id: 'condition',
      title: 'Condition Assessment',
      description: 'Vehicle condition and damage report',
      component: VehicleConditionAssessment,
      requiredFields: ['overallCondition', 'platesDeposited', 'serviceBook']
    },
    {
      id: 'photos',
      title: 'Photo Upload',
      description: 'Vehicle photos and documentation',
      component: VehiclePhotoUpload,
      // requiredFields: ['photos']
      requiredFields: []
    },
    {
      id: 'personal-info',
      title: 'Personal Information',
      description: 'Your contact details',
      component: PersonalInformation,
      requiredFields: ['name', 'email', 'phone']
    }
  ];

  // Load saved form data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('vehicleUploadForm');
    if (savedData) {
      try {
        setFormData(JSON.parse(savedData));
      } catch (error) {
        console.error('Error loading saved form data:', error);
      }
    }
  }, []);

  // Save form data to localStorage
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      localStorage.setItem('vehicleUploadForm', JSON.stringify(formData));
    }
  }, [formData]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleVinLookup = (vin) => {
    // Mock VIN lookup - in real app, this would call an API
    // setTimeout(() => {
    //   const mockVinData = {
    //     make: 'toyota',
    //     model: 'camry',
    //     year: '2020',
    //     transmission: 'automatic',
    //     fuelType: 'gasoline'
    //   };
      
    //   setFormData(prev => ({
    //     ...prev,
    //     ...mockVinData
    //   }));
    // }, 1000);
  };

  // Refactored to be a pure function that returns errors
  const validateStep = (stepIndex) => {
    const step = steps[stepIndex];
    const stepErrors = {};
    
    step.requiredFields.forEach(field => {
      if (field === 'photos') {
        const requiredPhotoTypes = ['front', 'rear', 'driver-side', 'passenger-side', 'interior-front', 'dashboard', 'odometer'];
        const uploadedPhotos = formData.photos || [];
        const uploadedTypes = uploadedPhotos.map(p => p.type);
        const missingRequired = requiredPhotoTypes.filter(type => !uploadedTypes.includes(type));
        
        if (missingRequired.length > 0) {
          stepErrors[field] = `Missing required photos: ${missingRequired.join(', ')}`;
        }
      } else if (!formData[field] || formData[field] === '') {
        stepErrors[field] = 'This field is required';
      }
    });

    // Additional validations
    if (stepIndex === 0) {
      if (formData.vin && formData.vin.length !== 17) {
        stepErrors.vin = 'VIN must be exactly 17 characters';
      }
      if (formData.mileage && (parseInt(formData.mileage) < 0 || parseInt(formData.mileage) > 500000)) {
        stepErrors.mileage = 'Please enter a valid mileage';
      }
    }

    return stepErrors;
  };

  const handleNext = async () => {
    const stepErrors = validateStep(currentStep);

    if (Object.keys(stepErrors).length === 0) {
      setErrors({});
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        handleSubmit();
      }
    } else {
      setErrors(stepErrors);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (stepIndex) => {
    // Only allow clicking on previous steps that have been completed
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const dataToSend = new FormData();
      for (const key in formData) {
        if (key === 'photos') {
          continue;
        }
        if (typeof formData[key] === 'boolean') {
          dataToSend.append(key, formData[key].toString());
        } else if (Array.isArray(formData[key])) {
          dataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          dataToSend.append(key, formData[key]);
        }
      }

      if (formData.photos && formData.photos.length > 0) {
        formData.photos.forEach((file, index) => {
          dataToSend.append('photos', file.file); 
        });
      }
       
      const response = await fetch(`${apiUrl}/api/offers`, {
        method: 'POST',
        body: dataToSend
      })
      
      if (!response.ok) {
        throw new Error('Failed to submit vehicle information. Please try again.');
      }
      
      // Clear saved form data
      localStorage.removeItem('vehicleUploadForm');
    
      //TODO: display success message, clear form

      // Navigate to offer tracking dashboard
      // navigate('/offer-tracking-dashboard', {
      //   state: { 
      //     message: 'Vehicle information submitted successfully! You will receive offers within 24 hours.',
      //     vehicleData: formData
      //   }
      // });
    } catch (error) {
      console.error('Submission error:', error);
      setErrors({ submit: 'Failed to submit vehicle information. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentStepComponent = () => {
    const StepComponent = steps[currentStep].component;
    return (
      <StepComponent
        formData={formData}
        onFormChange={handleFormChange}
        errors={errors}
        onVinLookup={handleVinLookup}
      />
    );
  };

  const getCompletedSteps = () => {
    // Refactored to not call validateStep and avoid the render loop
    return steps.filter((step, index) => {
      if (index >= currentStep) return false;
      const stepErrors = validateStep(index);
      return Object.keys(stepErrors).length === 0;
    }).length;
  };

  const getPrices = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/prices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching prices:', error);
      return [];
    }
  }
  

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
            <span>Sell Vehicle</span>
            <Icon name="ChevronRight" size={16} />
            <span className="text-foreground">Upload Information</span>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Sell Your Vehicle
          </h1>
          <p className="text-lg text-muted-foreground">
            Provide your vehicle details to receive guaranteed purchase offers within 24 hours
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Progress Indicator */}
            <FormProgressIndicator
              currentStep={currentStep}
              steps={steps}
              onStepClick={handleStepClick}
            />

            {/* Current Step Content */}
            <div className="space-y-6">
              {getCurrentStepComponent()}
            </div>

            {/* Navigation Buttons */}
            <div className="sticky bottom-0 bg-background border-t border-border p-4 -mx-4 lg:mx-0 lg:border-0 lg:bg-transparent lg:p-0">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  iconName="ChevronLeft"
                  iconPosition="left"
                >
                  Previous
                </Button>

                <div className="flex items-center space-x-4">
                  <span className="text-sm text-muted-foreground">
                    {getCompletedSteps()}/{steps.length} steps completed
                  </span>
                  
                  <Button
                    variant="default"
                    onClick={handleNext}
                    loading={isSubmitting}
                    iconName={currentStep === steps.length - 1 ? "Send" : "ChevronRight"}
                    iconPosition="right"
                  >
                    {currentStep === steps.length - 1 ? 'Submit & Get Offers' : 'Save & Continue'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {errors.submit && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-destructive">
                  <Icon name="AlertCircle" size={16} />
                  <span className="text-sm font-medium">{errors.submit}</span>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Valuation */}
          {displayValuation && (
            <div className="lg:col-span-1">
              <PreliminaryValuation
                formData={formData}
                isCollapsed={isValuationCollapsed}
                onToggleCollapse={() => setIsValuationCollapsed(!isValuationCollapsed)}
              />
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-12 bg-muted/50 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="HelpCircle" size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">Need Help?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Our team is here to help you through the vehicle selling process. Get assistance with:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                <li>• Vehicle information and documentation</li>
                <li>• Photo requirements and tips</li>
                <li>• Valuation questions</li>
                <li>• Technical support</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" iconName="Phone" iconPosition="left">
                  Call Support
                </Button>
                <Button variant="outline" iconName="MessageCircle" iconPosition="left">
                  Live Chat
                </Button>
                <Button variant="outline" iconName="Mail" iconPosition="left">
                  Email Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleInformationUpload;
